// src/merch/scanImport.js
//
// Colouring-sheet import: find the 4 black corner markers in a scan or
// phone photo, undo perspective (homography), crop to the sticker's bleed
// rect, then clean up: white-balance the paper, drop the light-blue
// guide lines, optionally make the paper see-through.
//
// Pure pixel functions ({width, height, data}) so they're testable
// outside the browser.

import { markerPointsMm, stripToBlock } from "./colouringSheet.js";
import { outlineDistance } from "./shapes.js";

/* ---------------- marker detection ---------------- */

/**
 * Find the sheet's corner markers.
 *   img     pixels (a downscaled copy is fine)
 *   aspect  expected width/height of the marker rectangle (see
 *           markerAspect) — used to validate candidates and, on sheets
 *           without an orientation mark, to guess a 90° turn
 * Returns { corners: [TL, TR, BR, BL] (sticker orientation, image px),
 *           oriented: true if the orientation mark was found } or null.
 */
export function detectMarkers(img, { aspect = 0 } = {}) {
  const { width: W, height: H, data } = img;
  const N = W * H;
  const lum = new Uint8Array(N);
  for (let i = 0, j = 0; j < N; i += 4, j++) lum[j] = (data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8;

  // adaptive threshold: dark relative to the local surroundings, so dim or
  // uneven light doesn't hide the markers (integral image box mean)
  const ii = new Float64Array((W + 1) * (H + 1));
  for (let y = 0; y < H; y++) {
    let row = 0;
    for (let x = 0; x < W; x++) {
      row += lum[y * W + x];
      ii[(y + 1) * (W + 1) + x + 1] = ii[y * (W + 1) + x + 1] + row;
    }
  }
  const R = Math.max(8, Math.round(Math.max(W, H) / 14));
  const dark = new Uint8Array(N);
  for (let y = 0; y < H; y++) {
    const y0 = Math.max(0, y - R), y1 = Math.min(H, y + R + 1);
    for (let x = 0; x < W; x++) {
      const x0 = Math.max(0, x - R), x1 = Math.min(W, x + R + 1);
      const sum = ii[y1 * (W + 1) + x1] - ii[y0 * (W + 1) + x1] - ii[y1 * (W + 1) + x0] + ii[y0 * (W + 1) + x0];
      const mean = sum / ((y1 - y0) * (x1 - x0));
      const l = lum[y * W + x];
      dark[y * W + x] = l < mean * 0.6 && l < 150 ? 1 : 0;
    }
  }

  // connected components → solid, roughly square blobs
  const seen = new Uint8Array(N);
  const stack = new Int32Array(N);
  const blobs = [];
  const minArea = Math.max(12, N * 0.00002);
  const maxArea = N * 0.03;
  for (let start = 0; start < N; start++) {
    if (seen[start] || !dark[start]) continue;
    let sp = 0, count = 0, sx = 0, sy = 0, sl = 0, x0 = W, y0 = H, x1 = 0, y1 = 0;
    stack[sp++] = start; seen[start] = 1;
    while (sp) {
      const p = stack[--sp];
      const x = p % W, y = (p / W) | 0;
      count++; sx += x; sy += y; sl += lum[p];
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      if (x > 0 && !seen[p - 1] && dark[p - 1]) { seen[p - 1] = 1; stack[sp++] = p - 1; }
      if (x < W - 1 && !seen[p + 1] && dark[p + 1]) { seen[p + 1] = 1; stack[sp++] = p + 1; }
      if (y > 0 && !seen[p - W] && dark[p - W]) { seen[p - W] = 1; stack[sp++] = p - W; }
      if (y < H - 1 && !seen[p + W] && dark[p + W]) { seen[p + W] = 1; stack[sp++] = p + W; }
    }
    if (count < minArea || count > maxArea) continue;
    const bw = x1 - x0 + 1, bh = y1 - y0 + 1, asp = bw / bh;
    // perspective + rotation can make a square's bbox less full: be lenient
    if (asp < 0.45 || asp > 2.2 || count / (bw * bh) < 0.5) continue;
    // real markers sit on paper: a thin ring just outside the blob must be
    // mostly bright. (Ring stays inside the gap to the orientation mark.)
    const m = Math.max(2, Math.round(Math.max(bw, bh) * 0.25));
    const blobL = sl / count;
    let ring = 0, bright = 0;
    const test = (x, y) => {
      if (x < 0 || y < 0 || x >= W || y >= H) return;
      ring++;
      const l = lum[y * W + x];
      if (l > blobL + 45 && !dark[y * W + x]) bright++;
    };
    const rx0 = x0 - m, rx1 = x1 + m, ry0 = y0 - m, ry1 = y1 + m;
    const step = Math.max(1, Math.round((rx1 - rx0) / 24));
    for (let x = rx0; x <= rx1; x += step) { test(x, ry0); test(x, ry1); }
    for (let y = ry0; y <= ry1; y += step) { test(rx0, y); test(rx1, y); }
    if (!ring || bright / ring < 0.75) continue;
    blobs.push({ x: sx / count + 0.5, y: sy / count + 0.5, area: count });
  }
  if (blobs.length < 4) return null;

  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const quadOk = (q) => {
    // convex, clockwise-ish, big enough, right proportions (either way up)
    const w = (dist(q[0], q[1]) + dist(q[3], q[2])) / 2;
    const h = (dist(q[0], q[3]) + dist(q[1], q[2])) / 2;
    if (w * h < N * 0.04) return false;
    for (let i = 0; i < 4; i++) {
      const a = q[i], b = q[(i + 1) % 4], c = q[(i + 2) % 4];
      if ((b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x) <= 0) return false;
    }
    if (!aspect) return true;
    const r = Math.log(w / h), e = Math.abs(Math.log(aspect));
    return Math.abs(Math.abs(r) - e) < Math.log(1.8);
  };

  // markers share a size: try each size cluster, take the biggest valid quad
  let best = null;
  const sorted = [...blobs].sort((a, b) => b.area - a.area);
  for (const ref of sorted) {
    const cl = blobs.filter((b) => b.area > ref.area / 2.2 && b.area < ref.area * 2.2);
    if (cl.length < 4) continue;
    const pick = (score) => cl.reduce((a, b) => (score(b) > score(a) ? b : a));
    const q = [pick((p) => -(p.x + p.y)), pick((p) => p.x - p.y), pick((p) => p.x + p.y), pick((p) => p.y - p.x)];
    if (new Set(q).size < 4 || !quadOk(q)) continue;
    const area = dist(q[0], q[2]) * dist(q[1], q[3]);
    if (!best || area > best.area) best = { q, area };
  }
  if (!best) return null;
  let q = best.q; // image order: TL, TR, BR, BL (clockwise)

  // consistency: in a (perspective-ish) parallelogram q0 − q1 + q2 − q3 ≈ 0.
  // A big residual means one corner is an impostor — but the residual
  // doesn't say which, so try swapping each corner for the best blob near
  // where the other three predict it, and keep the swap that fixes it.
  {
    const refArea = q.reduce((s, p) => s + p.area, 0) / 4;
    const resid = (qq) => {
      const d = (dist(qq[0], qq[2]) + dist(qq[1], qq[3])) / 2;
      return Math.hypot(qq[0].x - qq[1].x + qq[2].x - qq[3].x, qq[0].y - qq[1].y + qq[2].y - qq[3].y) / d;
    };
    const r0 = resid(q);
    if (r0 > 0.12) {
      let bestQ = null, bestR = r0;
      for (let i = 0; i < 4; i++) {
        const a = q[(i + 3) % 4], b = q[(i + 1) % 4], c = q[(i + 2) % 4];
        const pr = { x: a.x + b.x - c.x, y: a.y + b.y - c.y };
        let alt = null, altD = Infinity;
        for (const bl of blobs) {
          if (q.includes(bl) || bl.area < refArea / 2.5 || bl.area > refArea * 2.5) continue;
          const d = dist(bl, pr);
          if (d < altD) { altD = d; alt = bl; }
        }
        if (!alt) continue;
        const q2 = q.slice(); q2[i] = alt;
        const r = resid(q2);
        if (r < bestR && quadOk(q2)) { bestR = r; bestQ = q2; }
      }
      if (bestQ && bestR < r0 * 0.5) q = bestQ;
    }
  }

  // orientation mark: a small blob beside one marker, pointing clockwise
  // toward the next marker → that marker is the sheet's TOP-LEFT
  const mArea = q.reduce((s, p) => s + p.area, 0) / 4;
  const side = Math.sqrt(mArea);
  let rot = -1;
  for (const b of blobs) {
    if (b.area < mArea * 0.08 || b.area > mArea * 0.6) continue;
    for (let i = 0; i < 4; i++) {
      const m = q[i], nxt = q[(i + 1) % 4];
      const d = dist(b, m);
      if (d < side * 0.6 || d > side * 2.2) continue;
      const vx = (b.x - m.x) / d, vy = (b.y - m.y) / d;
      const L = dist(nxt, m), ux = (nxt.x - m.x) / L, uy = (nxt.y - m.y) / L;
      if (vx * ux + vy * uy > 0.85) { rot = i; break; }
    }
    if (rot >= 0) break;
  }
  let oriented = rot >= 0;
  if (!oriented && aspect && Math.abs(Math.log(aspect)) > 0.2) {
    // older sheet: at least fix a 90° turn using the proportions
    const w = (dist(q[0], q[1]) + dist(q[3], q[2])) / 2;
    const h = (dist(q[0], q[3]) + dist(q[1], q[2])) / 2;
    if (Math.sign(Math.log(w / h)) !== Math.sign(Math.log(aspect))) rot = 1;
  }
  if (rot > 0) q = [...q.slice(rot), ...q.slice(0, rot)];
  return { corners: q.map((p) => [p.x, p.y]), oriented };
}

/** Width/height of the marker rectangle for a sheet layout. */
export function markerAspect(layout) {
  return (layout.blockW + 2 * layout.g) / (layout.blockH + 2 * layout.g);
}

/* ---------------- homography ---------------- */

/** 3×3 H (row-major, h33 = 1) mapping src[i] → dst[i] for 4 points. */
export function homography(src, dst) {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i], [u, v] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  // Gaussian elimination with partial pivoting
  for (let c = 0; c < 8; c++) {
    let piv = c;
    for (let r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[piv][c])) piv = r;
    [A[c], A[piv]] = [A[piv], A[c]];
    [b[c], b[piv]] = [b[piv], b[c]];
    const d = A[c][c];
    if (Math.abs(d) < 1e-12) return null;
    for (let r = 0; r < 8; r++) {
      if (r === c) continue;
      const k = A[r][c] / d;
      if (!k) continue;
      for (let cc = c; cc < 8; cc++) A[r][cc] -= k * A[c][cc];
      b[r] -= k * b[c];
    }
  }
  const h = b.map((v, i) => v / A[i][i]);
  return [...h, 1];
}

/* ---------------- warp + clean ---------------- */

/**
 * Straighten + crop to the sticker bleed rect.
 *   img      source pixels
 *   corners  [TL,TR,BR,BL] marker centres in img px
 *   layout   sheetLayout(sticker)
 *   outW     output width in px (height follows the bleed-rect aspect)
 */
export function warpToSticker(img, corners, layout, outW) {
  const outH = Math.max(1, Math.round((outW * layout.BH) / layout.BW));
  const H = homography(markerPointsMm(layout), corners); // block-mm → image px
  if (!H) return null;
  const { width: W, height: Hh, data } = img;
  const out = new Uint8ClampedArray(outW * outH * 4);
  const sx = layout.BW / outW, sy = layout.BH / outH;
  for (let y = 0; y < outH; y++) {
    const vy = (y + 0.5) * sy;
    for (let x = 0; x < outW; x++) {
      // strip point → where it sits on paper (long stickers are split into rows)
      const [mx, my] = layout.rows > 1 ? stripToBlock(layout, (x + 0.5) * sx, vy) : [(x + 0.5) * sx, vy];
      const w = H[6] * mx + H[7] * my + 1;
      const u = (H[0] * mx + H[1] * my + H[2]) / w - 0.5;
      const v = (H[3] * mx + H[4] * my + H[5]) / w - 0.5;
      const o = (y * outW + x) * 4;
      if (u < 0 || v < 0 || u >= W - 1 || v >= Hh - 1) {
        out[o] = out[o + 1] = out[o + 2] = 255; out[o + 3] = 255;
        continue;
      }
      const x0 = u | 0, y0 = v | 0, fx = u - x0, fy = v - y0;
      const i00 = (y0 * W + x0) * 4, i10 = i00 + 4, i01 = i00 + W * 4, i11 = i01 + 4;
      for (let c = 0; c < 3; c++) {
        const top = data[i00 + c] + (data[i10 + c] - data[i00 + c]) * fx;
        const bot = data[i01 + c] + (data[i11 + c] - data[i01 + c]) * fx;
        out[o + c] = top + (bot - top) * fy;
      }
      out[o + 3] = 255;
    }
  }
  return { width: outW, height: outH, data: out };
}

/**
 * In-place clean-up.
 *   guides   guideShapes(sticker, layout) — outlines in sticker mm
 *   pxPerMm  output pixels per sticker mm
 *   bandMm   how far from an outline to look (covers print + alignment error)
 */
export function cleanScan(img, { removeGuides = true, seeThrough = false, guides = null, pxPerMm = 0, bandMm = 1 } = {}) {
  const { data } = img;
  const n = data.length / 4;

  // 1) even out the lighting (and white-balance) — see flattenLighting
  if (!flattenLighting(img)) globalWhiteBalance(img);

  // 2) erase the printed guides — only ALONG the known guide outlines, so
  // blue colouring elsewhere is untouched. Guide pixels are refilled from
  // nearby non-guide pixels (colour over a line stays solid, not striped).
  if (removeGuides && guides?.length && pxPerMm) {
    removeGuideLines(img, guides, pxPerMm, bandMm);
  }

  // 2b) snap paper (bright + unsaturated) to pure white: kills paper
  // grain, shadows' last few %, and ghosted guide edges
  for (let i = 0; i < data.length; i += 4) {
    const R = data[i], G = data[i + 1], B = data[i + 2];
    const mn = Math.min(R, G, B), mx = Math.max(R, G, B);
    if (mn > 215 && mx - mn < 30) { data[i] = data[i + 1] = data[i + 2] = 255; }
  }

  // 3) see-through paper: white → transparent, colours keep their strength
  if (seeThrough) {
    for (let i = 0; i < data.length; i += 4) {
      const m = Math.min(data[i], data[i + 1], data[i + 2]);
      const t = Math.min(1, Math.max(0, (255 - m - 20) / 70)); // 0 = paper, 1 = ink
      const a = t * t * (3 - 2 * t);
      data[i + 3] = a * 255;
      if (a > 0.01) {
        for (let c = 0; c < 3; c++) data[i + c] = 255 - (255 - data[i + c]) / a; // un-mix from white
      }
    }
  }
  return img;
}

/* ---------------- guide-line removal ---------------- */

// signed distance (mm) from a point to a guide outline
// The trim line prints as light neutral grey. Only tested inside the narrow
// band around the known outline, so colouring elsewhere is never touched.
// (Early test sheets used light blue guides; that rule was dropped because
// it also ate kids' blue colouring near the edge.)
function isGuideInk(R, G, B) {
  const mx = Math.max(R, G, B), mn = Math.min(R, G, B);
  return mn > 150 && mx - mn < 28;
}

function removeGuideLines(img, guides, pxPerMm, bandMm) {
  const { width: W, height: H, data } = img;
  const flag = new Uint8Array(W * H);
  let any = false;
  const dist = (mx, my) => {
    let d = Infinity;
    for (const g of guides) d = Math.min(d, outlineDistance(g.sticker, g.ox, g.oy, mx, my));
    return d;
  };
  // coarse grid first (outlines can have many segments): only pixels in
  // cells near the outline get the exact test
  const step = Math.max(0.5, bandMm);
  const gw = Math.ceil(W / pxPerMm / step) + 1, gh = Math.ceil(H / pxPerMm / step) + 1;
  const near = new Uint8Array(gw * gh);
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      near[gy * gw + gx] = dist((gx + 0.5) * step, (gy + 0.5) * step) <= bandMm + step * 0.75 ? 1 : 0;
    }
  }
  for (let y = 0; y < H; y++) {
    const my = (y + 0.5) / pxPerMm;
    const gy = Math.min(gh - 1, Math.floor(my / step));
    for (let x = 0; x < W; x++) {
      const mx = (x + 0.5) / pxPerMm;
      if (!near[gy * gw + Math.min(gw - 1, Math.floor(mx / step))]) continue;
      if (dist(mx, my) > bandMm) continue;
      const i = (y * W + x) * 4;
      if (isGuideInk(data[i], data[i + 1], data[i + 2])) { flag[y * W + x] = 1; any = true; }
    }
  }
  if (!any) return;

  // refill each flagged pixel from the nearest unflagged pixel in 8 directions
  const maxR = Math.ceil(bandMm * pxPerMm * 2) + 2;
  const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, -1], [1, -1], [-1, 1]];
  const fill = [];
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!flag[y * W + x]) continue;
      let r = 0, g = 0, b = 0, n = 0;
      for (const [dx, dy] of dirs) {
        for (let k = 1; k <= maxR; k++) {
          const xx = x + dx * k, yy = y + dy * k;
          if (xx < 0 || yy < 0 || xx >= W || yy >= H) break;
          if (flag[yy * W + xx]) continue;
          const j = (yy * W + xx) * 4;
          r += data[j]; g += data[j + 1]; b += data[j + 2]; n++;
          break;
        }
      }
      fill.push(y * W + x, n ? r / n : 255, n ? g / n : 255, n ? b / n : 255);
    }
  }
  for (let k = 0; k < fill.length; k += 4) {
    const i = fill[k] * 4;
    data[i] = fill[k + 1]; data[i + 1] = fill[k + 2]; data[i + 2] = fill[k + 3];
  }
}

/* ---------------- lighting ---------------- */

/**
 * Flatten uneven lighting: estimate the paper colour on a coarse grid
 * (cells showing blank paper), fill cells covered by colouring from their
 * paper neighbours (diffusion), smooth, then divide it out so paper reads
 * white everywhere. Handles gradients, vignetting, colour casts and soft
 * shadows; big coloured areas are safe because only paper cells are
 * measured. Sharp shadows (a hand) can't be fully fixed — hence the tips.
 * Returns false if there isn't enough visible paper to trust.
 */
function flattenLighting(img) {
  const { width: W, height: H, data } = img;
  const CELL = Math.max(8, Math.round(Math.max(W, H) / 60));
  const gw = Math.ceil(W / CELL), gh = Math.ceil(H / CELL);
  const n = gw * gh;
  const est = new Float32Array(n * 3); // paper colour per cell
  const bri = new Float32Array(n);
  const cand = new Uint8Array(n);

  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      const lums = [];
      for (let y = gy * CELL; y < Math.min(H, (gy + 1) * CELL); y += 2) {
        for (let x = gx * CELL; x < Math.min(W, (gx + 1) * CELL); x += 2) {
          const i = (y * W + x) * 4;
          lums.push([(data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8, i]);
        }
      }
      if (!lums.length) continue;
      lums.sort((a, b) => b[0] - a[0]);
      // brightest ~30% of the cell ≈ paper if any paper is there
      const top = lums.slice(0, Math.max(1, (lums.length * 0.3) | 0));
      let r = 0, g = 0, b = 0;
      for (const [, i] of top) { r += data[i]; g += data[i + 1]; b += data[i + 2]; }
      r /= top.length; g /= top.length; b /= top.length;
      const k = gy * gw + gx;
      est[k * 3] = r; est[k * 3 + 1] = g; est[k * 3 + 2] = b;
      bri[k] = (r + g + b) / 3;
      const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      const spread = top[0][0] - top[top.length - 1][0];
      // paper candidate: unsaturated, not dark, uniform brightest part
      cand[k] = mx > 55 && (mx - mn) / mx < 0.2 && spread < 45 ? 1 : 0;
    }
  }

  // keep candidates about as bright as nearby candidates
  // (rejects grey pencil shading sitting next to real paper)
  const paper = new Uint8Array(n);
  let count = 0;
  const R = 3;
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      const k = gy * gw + gx;
      if (!cand[k]) continue;
      let best = 0;
      for (let dy = -R; dy <= R; dy++) for (let dx = -R; dx <= R; dx++) {
        const x = gx + dx, y = gy + dy;
        if (x < 0 || y < 0 || x >= gw || y >= gh) continue;
        const j = y * gw + x;
        if (cand[j] && bri[j] > best) best = bri[j];
      }
      if (bri[k] >= best * 0.8) { paper[k] = 1; count++; }
    }
  }
  if (count < Math.max(10, n * 0.06)) return false;

  // fill non-paper cells by diffusion from paper cells (paper cells fixed)
  const grid = new Float32Array(est);
  const known = new Uint8Array(paper);
  for (let iter = 0; iter < 400; iter++) {
    let changed = false;
    for (let gy = 0; gy < gh; gy++) {
      for (let gx = 0; gx < gw; gx++) {
        const k = gy * gw + gx;
        if (paper[k]) continue;
        let r = 0, g = 0, b = 0, m = 0;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const x = gx + dx, y = gy + dy;
          if (x < 0 || y < 0 || x >= gw || y >= gh) continue;
          const j = y * gw + x;
          if (!known[j]) continue;
          r += grid[j * 3]; g += grid[j * 3 + 1]; b += grid[j * 3 + 2]; m++;
        }
        if (!m) continue;
        const nr = r / m, ng = g / m, nb = b / m;
        if (!known[k] || Math.abs(nr - grid[k * 3]) > 0.5) changed = true;
        grid[k * 3] = nr; grid[k * 3 + 1] = ng; grid[k * 3 + 2] = nb;
        known[k] = 1;
      }
    }
    if (!changed) break;
  }

  // light 3×3 smoothing so cell edges never show
  const sm = new Float32Array(n * 3);
  for (let gy = 0; gy < gh; gy++) for (let gx = 0; gx < gw; gx++) {
    let r = 0, g = 0, b = 0, m = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const x = gx + dx, y = gy + dy;
      if (x < 0 || y < 0 || x >= gw || y >= gh) continue;
      const j = y * gw + x;
      r += grid[j * 3]; g += grid[j * 3 + 1]; b += grid[j * 3 + 2]; m++;
    }
    const k = gy * gw + gx;
    sm[k * 3] = r / m; sm[k * 3 + 1] = g / m; sm[k * 3 + 2] = b / m;
  }

  // per-pixel gain, bilinear between cell centres
  const gains = new Float32Array(n * 3);
  for (let k = 0; k < n * 3; k++) gains[k] = Math.min(3.5, Math.max(0.85, 250 / Math.max(25, sm[k])));
  const cellAt = (gx, gy) => (Math.min(gh - 1, Math.max(0, gy)) * gw + Math.min(gw - 1, Math.max(0, gx))) * 3;
  for (let y = 0; y < H; y++) {
    const fy = y / CELL - 0.5, y0 = Math.floor(fy), ty = fy - y0;
    for (let x = 0; x < W; x++) {
      const fx = x / CELL - 0.5, x0 = Math.floor(fx), tx = fx - x0;
      const k00 = cellAt(x0, y0), k10 = cellAt(x0 + 1, y0), k01 = cellAt(x0, y0 + 1), k11 = cellAt(x0 + 1, y0 + 1);
      const i = (y * W + x) * 4;
      for (let c = 0; c < 3; c++) {
        const top = gains[k00 + c] + (gains[k10 + c] - gains[k00 + c]) * tx;
        const bot = gains[k01 + c] + (gains[k11 + c] - gains[k01 + c]) * tx;
        data[i + c] = data[i + c] * (top + (bot - top) * ty);
      }
    }
  }
  return true;
}

/** Fallback when too little paper shows: one global white balance. */
function globalWhiteBalance(img) {
  const { data } = img;
  const n = data.length / 4;
  const hist = new Uint32Array(256);
  for (let i = 0; i < data.length; i += 4) hist[(data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8]++;
  let acc = 0, cut = 255;
  for (let v = 255; v >= 0; v--) { acc += hist[v]; if (acc >= n * 0.15) { cut = v; break; } }
  let r = 0, g = 0, b = 0, k = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (((data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8) >= cut) { r += data[i]; g += data[i + 1]; b += data[i + 2]; k++; }
  }
  const gain = (v) => Math.min(2.5, 250 / Math.max(40, v / Math.max(1, k)));
  const gr = gain(r), gg = gain(g), gb = gain(b);
  for (let i = 0; i < data.length; i += 4) {
    data[i] = data[i] * gr; data[i + 1] = data[i + 1] * gg; data[i + 2] = data[i + 2] * gb;
  }
}
